import React, { useEffect, useRef, useState } from "react";
import { GetImageUrl } from "../helpers/GetImageUrl";

const ReviewCard = ({ review }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef(null);
  const reviewText = review?.review || "";
  const hasPhotos = (review?.photos?.length || 0) > 0;

  useEffect(() => {
    if (isExpanded || !textRef.current) return;

    const textElement = textRef.current;
    const measureOverflow = () => {
      setIsOverflowing(textElement.scrollHeight > textElement.clientHeight + 1);
    };
    const animationFrame = window.requestAnimationFrame(measureOverflow);
    const resizeObserver = new ResizeObserver(measureOverflow);
    resizeObserver.observe(textElement);

    return () => {
      window.cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
    };
  }, [isExpanded, reviewText]);

  const toggleExpanded = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsExpanded((current) => !current);
  };

  return (
    <article className="flex h-auto min-h-70 flex-col overflow-visible rounded border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
      
      {/* Reviewer */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {review?.profilePhoto && (
            <img
              src={review.profilePhoto}
              alt={review?.name || "Reviewer"}
              className="h-11 w-11 shrink-0 rounded-full object-cover"
            />
          )}

          <div className="min-w-0">
            <h3 className="font-semibold text-slate-900 truncate">
              {review?.name}
            </h3>

            {review?.source === "google" && (
              <p className="mt-0.5 text-xs font-medium text-slate-400">
                Google Review
              </p>
            )}
          </div>
        </div>

        {/* Rating */}
        <div className="flex shrink-0 text-lg text-amber-400">
          {Array.from({ length: 5 }).map((_, i) => (
            <span
              key={i}
              className={
                i < Number(review?.rating || 0)
                  ? ""
                  : "text-slate-200"
              }
            >
              ★
            </span>
          ))}
        </div>
      </div>

      {/* Review Text */}
      <p
        ref={textRef}
        className={`mt-4 ${
          isExpanded
            ? "whitespace-pre-line wrap-break-word text-sm leading-7 text-slate-600"
            : "min-h-28 line-clamp-4 text-sm leading-7 text-slate-600"
        }`}
      >
        {reviewText}
      </p>

      {/* Existing local review uploaded photos */}
      {isExpanded && review?.photos?.length > 0 && (
        <div className="mt-5 grid grid-cols-2 gap-3">
          {review.photos.map((photo) => (
            <img
              key={photo}
              src={GetImageUrl(photo)}
              alt="Review"
              className="aspect-video w-full rounded-xl object-cover"
            />
          ))}
        </div>
      )}

      {/* Show More */}
      {(isExpanded || isOverflowing || hasPhotos) && (
        <button
          type="button"
          onClick={toggleExpanded}
          aria-expanded={isExpanded}
          className="mt-5 self-start text-sm font-semibold text-blue-600 transition hover:text-blue-700"
        >
          {isExpanded ? "See Less" : "See More"}
        </button>
      )}

      {/* Local review details */}
      {(review?.serviceUsed || review?.productId?.title) && (
        <div className="mt-auto border-t border-slate-100 pt-4">
          <div className="flex flex-wrap gap-2">
            {review?.serviceUsed && (
              <span className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium text-slate-700">
                {review.serviceUsed}
              </span>
            )}

            {review?.productId?.title && (
              <span className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium text-slate-700">
                {review.productId.title}
              </span>
            )}
          </div>
        </div>
      )}
    </article>
  );
};

export default ReviewCard;